import type { Widget, WidgetType } from '../../../api/dashboards.js';

/** Generate a cryptographically-ish random widget id. */
export function newWidgetId(): string {
  const rnd = Math.random().toString(36).slice(2, 10);
  return `w_${Date.now().toString(36)}_${rnd}`;
}

/** Default widget factory per type — used when dragging from the palette. */
export function buildDefaultWidget(
  type: WidgetType,
  datasetKey: string,
  position: { x: number; y: number },
): Widget {
  const id = newWidgetId();
  const base = {
    id,
    title: defaultTitle(type),
    position: { ...position, w: defaultSize(type).w, h: defaultSize(type).h },
    binding: { datasetKey },
  };
  switch (type) {
    case 'kpi':
      return {
        ...base,
        type,
        config: {
          valueField: '',
          valueAgg: 'sum',
          format: 'number',
          deltaDirection: 'positive-up',
        },
      };
    case 'bar':
    case 'line':
    case 'area':
      return {
        ...base,
        type,
        config: {
          xField: '',
          yField: '',
          yAgg: 'sum',
          orientation: 'vertical',
          stacked: false,
          showLegend: true,
          showGrid: true,
          yFormat: 'number',
        },
      };
    case 'pie':
    case 'donut':
      return {
        ...base,
        type,
        config: {
          categoryField: '',
          valueField: '',
          valueAgg: 'sum',
          showLegend: true,
          showLabels: false,
          maxSlices: 8,
        },
      };
    case 'table':
      return {
        ...base,
        type,
        config: { columns: [], rowLimit: 100, zebra: true, dense: false },
      };
    case 'text':
      return {
        id,
        title: 'Text',
        position: { ...position, w: defaultSize(type).w, h: defaultSize(type).h },
        type,
        config: { markdown: 'Type some **markdown** here.', align: 'left', size: 'md' },
      };
  }
}

function defaultTitle(type: WidgetType): string {
  switch (type) {
    case 'kpi':
      return 'New KPI';
    case 'bar':
      return 'Bar chart';
    case 'line':
      return 'Line chart';
    case 'area':
      return 'Area chart';
    case 'pie':
      return 'Pie chart';
    case 'donut':
      return 'Donut chart';
    case 'table':
      return 'Table';
    case 'text':
      return 'Text';
  }
}

export function defaultSize(type: WidgetType): { w: number; h: number } {
  switch (type) {
    case 'kpi':
      return { w: 3, h: 2 };
    case 'bar':
    case 'line':
    case 'area':
      return { w: 6, h: 4 };
    case 'pie':
    case 'donut':
      return { w: 4, h: 4 };
    case 'table':
      return { w: 8, h: 5 };
    case 'text':
      return { w: 4, h: 2 };
  }
}
